import { z } from 'zod';
import { AloftObject } from './AloftObject.js';
import { Lens } from './Lens.js';
import { unreachable } from './utils/unreachable.js';
const schAreaUnit = z.enum(['sqft', 'acre']);
export const schAloftAreaJSON = z.object({
    $type: z.literal('AloftArea'),
    unit: schAreaUnit,
    unitAmount: z.number(),
});
const SQFT_PER_ACRE = 43_560;
const getShortUnit = (unit) => {
    switch (unit) {
        case 'sqft':
            return 'sf';
        case 'acre':
            return 'ac';
        default:
            return unreachable(unit);
    }
};
export class AloftArea extends AloftObject {
    unitAmount;
    unit;
    constructor(unitAmount, unit) {
        super();
        this.unitAmount = unitAmount;
        this.unit = unit;
    }
    static isAloftArea(x) {
        return x instanceof AloftArea;
    }
    static min(areas) {
        const definedAreas = areas.filter((area) => area !== null && area !== undefined);
        if (definedAreas.length === 0)
            return undefined;
        return definedAreas.reduce((min, area) => (area.lt(min) ? area : min), definedAreas[0]);
    }
    static max(areas) {
        const definedAreas = areas.filter((area) => area !== null && area !== undefined);
        if (definedAreas.length === 0)
            return undefined;
        return definedAreas.reduce((max, area) => (area.gt(max) ? area : max), definedAreas[0]);
    }
    compare(that) {
        if (this.unit === that.unit)
            return this.unitAmount - that.unitAmount;
        return this.toSqFt().unitAmount - that.toSqFt().unitAmount;
    }
    // NOTE: toJSON() and schema.parse() should be inverses of each other!
    toJSON() {
        return { $type: 'AloftArea', unitAmount: this.unitAmount, unit: this.unit };
    }
    static schema = z
        .union([schAloftAreaJSON, z.custom((x) => x instanceof AloftArea)])
        .transform(({ unitAmount, unit }) => new AloftArea(unitAmount, unit));
    static zero = new AloftArea(0, 'sqft');
    static fromNumber(n, unit) {
        if (n === null || n === undefined)
            return n;
        return new AloftArea(n, unit);
    }
    static fromString(s, unit) {
        if (s === null || s === undefined)
            return s;
        const normalized = s.replaceAll(/,/g, '');
        const n = Number.parseFloat(normalized);
        if (Number.isNaN(n))
            throw new Error(`Cannot parse number: ${s} (normalized: ${normalized})`);
        return new AloftArea(n, unit);
    }
    toSqFt() {
        switch (this.unit) {
            case 'sqft':
                return this;
            case 'acre':
                return new AloftArea(Math.round(this.unitAmount * SQFT_PER_ACRE), 'sqft');
            default:
                return unreachable(this.unit);
        }
    }
    toAcre() {
        switch (this.unit) {
            case 'sqft':
                return new AloftArea(this.unitAmount / SQFT_PER_ACRE, 'acre');
            case 'acre':
                return this;
            default:
                return unreachable(this.unit);
        }
    }
    toUnit(unit) {
        switch (unit) {
            case 'sqft':
                return this.toSqFt();
            case 'acre':
                return this.toAcre();
            default:
                return unreachable(unit);
        }
    }
    add(...areas) {
        const rhs = areas.map((a) => a.toUnit(this.unit).unitAmount).reduce((a, b) => a + b, 0);
        return new AloftArea(this.unitAmount + rhs, this.unit);
    }
    sub(that) {
        return new AloftArea(this.unitAmount - that.toUnit(this.unit).unitAmount, this.unit);
    }
    mul(by) {
        if (by === undefined || by === null)
            return by;
        return new AloftArea(this.unitAmount * by, this.unit);
    }
    toString(shortUnit = false, separator = ' ') {
        return `${this.unitAmount.toLocaleString('en-US')}${separator}${shortUnit ? getShortUnit(this.unit) : this.unit}`;
    }
    toRoundedString(shortUnit = false, separator = ' ') {
        return `${Math.round(this.unitAmount).toLocaleString('en-US')}${separator}${shortUnit ? getShortUnit(this.unit) : this.unit}`;
    }
    toSqFtValueStringRounded() {
        return Math.round(this.toSqFt().unitAmount).toLocaleString('en-US');
    }
    toAcreValueString() {
        // Round to two decimal places
        return (Math.round(this.toAcre().unitAmount * 100) / 100).toLocaleString('en-US');
    }
    getPreferredUnit() {
        return this.toSqFt().unitAmount > SQFT_PER_ACRE ? 'acre' : 'sqft';
    }
    getSiteAreaString(separator = ' ') {
        const preferredUnit = this.getPreferredUnit();
        if (preferredUnit === 'acre') {
            return `${this.toAcreValueString()}${separator}ac`;
        }
        return `${this.toSqFtValueStringRounded()}${separator}sf`;
    }
    getUnitAmount(unit = 'sqft') {
        return this.toUnit(unit).unitAmount;
    }
    static sqftLens = new Lens((area) => area.getUnitAmount('sqft'), (area, sqft) => AloftArea.fromNumber(sqft, 'sqft').toUnit(area.unit));
    static acreLens = new Lens((area) => area.getUnitAmount('acre'), (area, acre) => AloftArea.fromNumber(acre, 'acre').toUnit(area.unit));
    static sqftLensOpt = AloftArea.sqftLens.optional(AloftArea.fromNumber(0, 'sqft'));
    static acreLensOpt = AloftArea.sqftLens.optional(AloftArea.fromNumber(0, 'acre'));
    static sqftLensNull = AloftArea.sqftLens.nullable(AloftArea.fromNumber(0, 'sqft'));
    static acreLensNull = AloftArea.sqftLens.nullable(AloftArea.fromNumber(0, 'acre'));
}
//# sourceMappingURL=AloftArea.js.map